import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { useApi, useMutation } from '../lib/use-api';
import { formatDateTime } from '../lib/format';
import { EmptyNotice, ErrorNotice, Loading } from '../components/Feedback';
import { AssignmentDialog, type AssignmentFormValue } from '../components/AssignmentDialog';

export interface AssignmentRow {
  id: string;
  titleI18n: Record<string, string>;
  audience: string;
  audienceRefId: string | null;
  audienceName: string | null;
  accessSlug: string;
  status: 'draft' | 'published' | 'archived';
  openAt: string | null;
  closeAt: string | null;
  maxAttempts: number | null;
  masteryScore: number | null;
  allowResume: boolean;
  timeLimitSec: number | null;
  attemptCount: number;
  updatedAt: string;
}

/**
 * URL người học mở để vào thẳng bài (§6.5).
 *
 * Lấy từ `VITE_PORTAL_PUBLIC_URL` chứ không dựng từ `window.location`:
 * web-admin và web-portal là hai app khác cổng, dựng từ origin hiện tại sẽ ra
 * link trỏ vào chính web-admin — link vẫn "trông đúng" nên lỗi này chỉ lộ khi
 * có người thật bấm vào.
 */
function shareUrl(accessSlug: string): string {
  const base = import.meta.env['VITE_PORTAL_PUBLIC_URL'];
  const origin = (typeof base === 'string' && base ? base : 'http://localhost:5174').replace(
    /\/+$/,
    '',
  );
  return `${origin}/p/${accessSlug}`;
}

/**
 * Bài giao — plan §6.3.
 *
 * "Xuất bản" ở đây là đổi `status` từ `draft` sang `published`. Bài draft chỉ
 * người tạo thấy; người học gọi API cũng nhận 404.
 */
export function AssignmentsPage(): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.resolvedLanguage ?? 'vi';
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AssignmentFormValue | undefined>(undefined);
  const [copiedId, setCopiedId] = useState('');

  /**
   * Sao chép link chia sẻ.
   *
   * `navigator.clipboard` chỉ tồn tại ở secure context — http://localhost tính
   * là secure, nhưng máy dev truy cập qua IP LAN thì KHÔNG, và ở đó API này
   * `undefined`. Không có nhánh dự phòng thì nút im lặng không làm gì.
   */
  const copyLink = async (row: AssignmentRow): Promise<void> => {
    const url = shareUrl(row.accessSlug);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(row.id);
      window.setTimeout(() => setCopiedId(''), 2000);
    } catch {
      window.prompt(t('assignment.copyFallback'), url);
    }
  };

  const assignments = useApi<{ items: AssignmentRow[] }>('/api/assignments');

  const setStatus = useMutation<{ id: string; status: string }, boolean>(
    useCallback(
      async (input: { id: string; status: string }) => {
        await apiFetch<void>(`/api/assignments/${input.id}/status`, {
          method: 'PUT',
          body: { status: input.status },
        });
        return true;
      },
      [],
    ),
  );

  const remove = useMutation<string, void>(
    useCallback((id: string) => apiFetch<void>(`/api/assignments/${id}`, { method: 'DELETE' }), []),
  );

  const changeStatus = async (id: string, status: string): Promise<void> => {
    const succeeded = await setStatus.run({ id, status });
    if (succeeded) assignments.reload();
  };

  const title = (row: AssignmentRow): string =>
    row.titleI18n[locale] ?? row.titleI18n['vi'] ?? Object.values(row.titleI18n)[0] ?? '';

  /**
   * Xoá bài giao.
   *
   * Server tự chặn 409 khi bài đã có lượt làm (xoá sẽ CASCADE mất luôn điểm),
   * nên nút vẫn hiện ở mọi dòng và lỗi hiện ra khi bấm — thà báo rõ lý do còn
   * hơn ẩn nút để người dùng đoán vì sao không xoá được.
   */
  const confirmRemove = async (row: AssignmentRow): Promise<void> => {
    if (!window.confirm(t('assignment.confirmDelete', { title: title(row) }))) return;
    await remove.run(row.id);
    assignments.reload();
  };

  const toFormValue = (row: AssignmentRow): AssignmentFormValue => ({
    id: row.id,
    titleI18n: row.titleI18n,
    audience: row.audience,
    audienceRefId: row.audienceRefId,
    openAt: row.openAt,
    closeAt: row.closeAt,
    maxAttempts: row.maxAttempts,
    masteryScore: row.masteryScore,
    allowResume: row.allowResume,
    timeLimitSec: row.timeLimitSec,
  });

  return (
    <section>
      <h1>{t('assignment.heading')}</h1>

      <div className="toolbar">
        <button type="button" onClick={() => setCreating(true)}>
          {t('assignment.create')}
        </button>
      </div>

      {setStatus.error ? <ErrorNotice error={setStatus.error} /> : null}
      {assignments.loading ? <Loading /> : null}
      {assignments.error ? (
        <ErrorNotice error={assignments.error} onRetry={assignments.reload} />
      ) : null}

      {assignments.data && !assignments.loading ? (
        assignments.data.items.length === 0 ? (
          <EmptyNotice message={t('assignment.empty')} />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t('assignment.column.title')}</th>
                <th scope="col">{t('assignment.column.audience')}</th>
                <th scope="col">{t('assignment.column.status')}</th>
                <th scope="col">{t('assignment.column.attempts')}</th>
                <th scope="col">{t('assignment.column.updatedAt')}</th>
                <th scope="col">{t('library.column.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {assignments.data.items.map((row) => (
                <tr key={row.id}>
                  <td>{title(row)}</td>
                  <td>
                    {row.audience === 'public'
                      ? t('assignment.audience.public')
                      : (row.audienceName ?? '—')}
                  </td>
                  <td>{t(`assignment.status.${row.status}`)}</td>
                  <td>{row.attemptCount}</td>
                  <td>{formatDateTime(row.updatedAt, locale)}</td>
                  <td className="actions">
                    {/* Link chỉ có nghĩa khi bài đã xuất bản — bài draft trả
                        404 cho người học, đưa link ra là mời họ bấm vào lỗi. */}
                    {row.status === 'published' ? (
                      <button
                        type="button"
                        title={shareUrl(row.accessSlug)}
                        onClick={() => void copyLink(row)}
                      >
                        {copiedId === row.id ? t('assignment.copied') : t('assignment.copyLink')}
                      </button>
                    ) : null}
                    {row.status === 'draft' ? (
                      <button type="button" onClick={() => void changeStatus(row.id, 'published')}>
                        {t('assignment.publish')}
                      </button>
                    ) : null}
                    {row.status === 'published' ? (
                      <button type="button" onClick={() => void changeStatus(row.id, 'archived')}>
                        {t('assignment.archive')}
                      </button>
                    ) : null}
                    {row.status === 'archived' ? (
                      <button type="button" onClick={() => void changeStatus(row.id, 'published')}>
                        {t('assignment.republish')}
                      </button>
                    ) : null}
                    <button type="button" onClick={() => setEditing(toFormValue(row))}>
                      {t('common:action.edit')}
                    </button>
                    <button
                      type="button"
                      disabled={remove.pending}
                      onClick={() => void confirmRemove(row)}
                    >
                      {t('common:action.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}

      {creating ? (
        <AssignmentDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            assignments.reload();
          }}
        />
      ) : null}

      {editing ? (
        <AssignmentDialog
          assignment={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            assignments.reload();
          }}
        />
      ) : null}
    </section>
  );
}
